<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Mensagens Pré Definidas</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Crie e mantenha modelos reutilizáveis para acelerar respostas e comunicações
            recorrentes.
          </p>
        </div>

        <button
          type="button"
          class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md sm:w-auto"
          @click="voltarParaConfig"
        >
          <svg
            class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Voltar
        </button>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div class="flex flex-col gap-1 md:col-span-2">
            <label class="text-sm font-medium text-gray-500">Buscar</label>
            <input
              v-model="filtroBusca"
              type="text"
              placeholder="Título ou conteúdo"
              class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div v-if="temCentroCusto" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-500">Centro de custo</label>
            <div class="relative group">
              <select
                v-model="filtroCentroDeCusto"
                class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
              >
                <option value="">Todas</option>
                <option
                  v-for="centro in centroscustos"
                  :key="centro._id"
                  :value="centro._id"
                >
                  {{ centro.nome }}
                </option>
              </select>
              <svg
                class="select-icon pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 transition-all duration-200 group-focus-within:text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          <div class="flex items-end gap-2 md:col-span-4 lg:col-span-1">
            <button
              @click="aplicarFiltros"
              class="h-11 w-full rounded-xl font-medium text-white transition"
              :style="{ backgroundColor: primaryColor }"
              @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
              @mouseleave="$event.target.style.backgroundColor = primaryColor"
            >
              Buscar
            </button>
            <button
              @click="limparFiltros"
              class="h-11 rounded-xl border border-gray-200 bg-white px-4 font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      <div class="mb-6 flex justify-end">
        <router-link
          :to="`/premensagem/form`"
          class="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm transition hover:shadow-md sm:w-auto"
        >
          <HeroPlusCircle class="h-6 w-6 text-green-600" />
          <span class="text-sm font-medium text-gray-700">Nova mensagem</span>
        </router-link>
      </div>

      <div class="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">Mensagem</th>
                <th class="px-6 py-4 text-left font-medium">Centro de custo</th>
                <th class="px-6 py-4 text-left font-medium">Atualização</th>
                <th class="px-6 py-4 text-center font-medium">Status</th>
                <th class="px-6 py-4 text-center font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in mensagensPaginadas"
                :key="item._id"
                class="border-b border-gray-100 transition hover:bg-slate-50"
              >
                <td class="px-6 py-5">
                  <div class="flex items-center gap-4">
                    <div
                      class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700"
                    >
                      {{ String(item.titulo || "--").slice(0, 2).toUpperCase() }}
                    </div>
                    <div class="flex flex-col">
                      <span class="font-medium text-gray-800">{{ item.titulo }}</span>
                      <span class="max-w-xs truncate text-xs text-gray-400 xl:max-w-md">{{
                        item.mensagem
                      }}</span>
                    </div>
                  </div>
                </td>
                <td class="px-6 font-medium text-gray-700">
                  {{
                    item.centrocusto && item.centrocusto.nome
                      ? item.centrocusto.nome
                      : "Geral"
                  }}
                </td>
                <td class="px-6 font-medium text-gray-700">{{ formatarData(item.updatedAt) }}</td>
                <td class="px-6 text-center">
                  <span
                    class="rounded-full px-3 py-1 text-xs font-medium"
                    :class="
                      item.ativo
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    "
                  >
                    {{ item.ativo ? "Ativa" : "Inativa" }}
                  </span>
                </td>
                <td class="px-6 py-5">
                  <div class="flex flex-wrap justify-center gap-2">
                    <router-link
                      :to="`/premensagem/form/${item._id}`"
                      class="rounded-lg px-4 py-2 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: primaryColor }"
                      @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                      @mouseleave="$event.target.style.backgroundColor = primaryColor"
                      >Editar</router-link
                    >
                    <button
                      @click="remove(item._id)"
                      class="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-800"
                    >
                      Remover
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="mensagensPaginadas.length === 0">
                <td colspan="5" class="px-6 py-12 text-center text-sm text-gray-400">
                  Nenhuma mensagem encontrada com os filtros atuais.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        :currentPage="paginaAtual"
        :total-pages="totalPaginas"
        :total-items="mensagensFiltradas.length"
        :page-size="itensPorPagina"
        item-label="mensagens"
        @update:currentPage="paginaAtual = $event"
      />
    </div>
  </div>
</template>

<script>
import AppPagination from "@/components/AppPagination.vue";
import { HeroPlusCircle } from "@/components/icons/heroicons";
import { colorMixin } from '@/utils/colorMixin';

export default {
  mixins: [colorMixin],
  components: {
    AppPagination,
    HeroPlusCircle,
  },
  data() {
    return {
      filtroBusca: "",
      filtroCentroDeCusto: "",
      paginaAtual: 1,
      itensPorPagina: 5,
      filtrosAplicados: { busca: "", centroDeCusto: "" },
      mensagens: [],
      centroscustos: [],
    };
  },
  computed: {
    temCentroCusto() {
      const user = this.$store.state.user || {};
      return !!(user.lzv2 && user.lzv2.centrocusto);
    },
    mensagensFiltradas() {
      return this.mensagens.filter((item) => {
        const busca = this.filtrosAplicados.busca.toLowerCase();
        const matchBusca =
          !busca ||
          item.titulo.toLowerCase().includes(busca) ||
          (item.mensagem && item.mensagem.toLowerCase().includes(busca));
        const matchCentroDeCusto =
          !this.filtrosAplicados.centroDeCusto ||
          (item.centrocusto &&
            item.centrocusto._id === this.filtrosAplicados.centroDeCusto);
        return matchBusca && matchCentroDeCusto;
      });
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.mensagensFiltradas.length / this.itensPorPagina));
    },
    mensagensPaginadas() {
      return this.mensagensFiltradas.slice(
        (this.paginaAtual - 1) * this.itensPorPagina,
        this.paginaAtual * this.itensPorPagina
      );
    },
  },
  methods: {
    async start() {
      try {
        const response = await this.$http.post("/v1/preMensagem/list");
        this.mensagens = response.data.data || [];

        if (this.temCentroCusto) {
          const centrosReq = await this.$http.post("/v1/centrocusto/list", { all: true });
          this.centroscustos = centrosReq.data.data || [];
        }
      } catch (err) {
        console.error("Erro ao carregar mensagens:", err);
        this.mensagens = [];
        this.centroscustos = [];
      }
    },
    aplicarFiltros() {
      this.filtrosAplicados = {
        busca: this.filtroBusca.trim(),
        centroDeCusto: this.filtroCentroDeCusto,
      };
      this.paginaAtual = 1;
    },
    limparFiltros() {
      this.filtroBusca = "";
      this.filtroCentroDeCusto = "";
      this.filtrosAplicados = { busca: "", centroDeCusto: "" };
      this.paginaAtual = 1;
    },
    async remove(id) {
      if (confirm("Tem certeza que deseja excluir esta mensagem?")) {
        try {
          const resp = await this.$http.put("/v1/preMensagem", { _id: id, ativo: false });
          if (!resp.data.success) {
            alert("Erro: " + resp.data.err);
            return;
          }
          alert("Mensagem removida com sucesso");
          this.start();
        } catch (err) {
          console.error("Erro ao remover:", err);
          alert("Erro ao remover mensagem");
        }
      }
    },
    formatarData(data) {
      if (!data) return "";
      const date = new Date(data);
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    voltarParaConfig() {
      this.$router.push({ name: "Config" });
    },
  },
  beforeMount() {
    this.start();
  },
};
</script>

<style scoped>
:deep(.heroicon-ui) {
  fill: currentColor !important;
}

.icon-anim {
  transition: transform 0.2s ease;
}

.icon-anim-trigger--back:hover .icon-anim--back,
.icon-anim-trigger--back:focus-visible .icon-anim--back {
  transform: translateX(-4px);
}

.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
